/**
 * Joueur DAG élève (orchestrateur). Énoncé persistant en haut, puis le nœud
 * courant (principal ou sous-nœud), un panneau de feedback gâté, et le bilan.
 * Mobile-first : conteneur étroit, actions w-full, safe-area en bas.
 */
import { useMemo, useState } from 'react';
import { Button, cn, MarkdownMath } from '@jeraisonne/ui';
import { Loader2, GitBranch, ChevronDown, CheckCircle2, XCircle, Lock } from 'lucide-react';
import { useDagPlayer } from './use-dag-player';
import { usePaperPlayerForMe } from '@/features/paper-player';
import { NoeudView } from './noeud-view';
import { SubNoeudView } from './sub-noeud-view';
import { ResultSummary } from './result-summary';
import type { PlayerDag, PlayerNoeud } from './types';

function subnodesOf(dag: PlayerDag, mainId: string): PlayerNoeud[] {
  return dag.noeuds
    .filter((n) => n.is_sub && n.subnode_of === mainId)
    .sort((a, b) => a.ordre - b.ordre);
}

const LOAD_ERROR_LABEL: Record<string, string> = {
  access_denied: 'Tu n’as pas encore accès à cet exercice.',
  no_dag: 'Cet exercice n’a pas (encore) de parcours guidé.',
  not_found: 'Exercice introuvable.',
  not_published: 'Cet exercice n’est pas encore publié.',
  unauthenticated: 'Connecte-toi pour jouer ce parcours.',
};

export function DagPlayer({
  exerciseId,
  onReplay,
  onExit,
}: {
  exerciseId: string;
  onReplay: () => void;
  onExit: () => void;
}) {
  const player = useDagPlayer(exerciseId);
  const [enonceOpen, setEnonceOpen] = useState(true);
  // Exercice « sur la feuille de brouillon » (drapeau `paper_player`).
  const paper = usePaperPlayerForMe();

  const { dag, currentNode, currentChoix, lastResult } = player;

  const allowHelp = useMemo(() => {
    if (!dag || !currentNode || currentNode.is_sub) return false;
    return (
      currentNode.subnode_trigger === 'on_request' && subnodesOf(dag, currentNode.id).length > 0
    );
  }, [dag, currentNode]);

  const subInfo = useMemo(() => {
    if (!dag || !currentNode || !currentNode.is_sub || !currentNode.subnode_of) return null;
    const chain = subnodesOf(dag, currentNode.subnode_of);
    const idx = chain.findIndex((n) => n.id === currentNode.id);
    return { step: idx + 1, total: chain.length };
  }, [dag, currentNode]);

  return (
    <div
      className={cn(
        'mx-auto max-w-2xl px-4 py-6 pb-[max(env(safe-area-inset-bottom),1.5rem)] sm:py-8',
        paper && 'pp-root pp-page pp-column min-h-dvh max-w-none',
      )}
    >
      <header className="mb-4 flex items-center gap-2.5">
        <div className="bg-primary-50 flex h-9 w-9 items-center justify-center rounded-xl">
          <GitBranch className="text-primary-500 h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <h1 className="font-display text-ink-900 text-lg font-extrabold leading-tight">
            Parcours guidé
          </h1>
          {dag && (
            <p className="text-ink-500 text-xs">
              Niveau {dag.dag.niveau} · {player.score} pts
            </p>
          )}
        </div>
      </header>

      {/* Énoncé persistant, repliable */}
      {dag?.enonce && (
        <section className="border-ink-200 mb-4 rounded-2xl border bg-white">
          <button
            type="button"
            onClick={() => setEnonceOpen((v) => !v)}
            aria-expanded={enonceOpen}
            className="text-ink-700 flex min-h-[2.75rem] w-full items-center justify-between gap-2 px-4 text-sm font-semibold active:scale-[0.99]"
          >
            Énoncé
            <ChevronDown
              className={cn('h-4 w-4 transition-transform', enonceOpen && 'rotate-180')}
            />
          </button>
          {enonceOpen && (
            <div className="text-ink-800 px-4 pb-4 text-sm leading-relaxed">
              <MarkdownMath>{dag.enonce}</MarkdownMath>
            </div>
          )}
        </section>
      )}

      {/* Corps */}
      {player.status === 'loading' && (
        <div className="text-ink-400 flex items-center justify-center gap-2 py-16 text-sm">
          <Loader2 className="h-5 w-5 animate-spin" /> Chargement du parcours…
        </div>
      )}

      {player.status === 'error' && (
        <div className="border-ink-200 space-y-3 rounded-2xl border bg-white p-5 text-center">
          <Lock className="text-ink-400 mx-auto h-8 w-8" />
          <p className="text-ink-700 text-sm">
            {LOAD_ERROR_LABEL[player.loadError ?? ''] ?? 'Impossible de charger ce parcours.'}
          </p>
          <Button variant="secondary" fullWidth onClick={onExit}>
            Retour
          </Button>
        </div>
      )}

      {player.status === 'completed' && (
        <ResultSummary
          score={player.score}
          xpGain={player.completion?.xp_gain ?? null}
          answers={player.answers}
          onReplay={onReplay}
          onExit={onExit}
        />
      )}

      {player.status === 'playing' && currentNode && (
        <section className="border-ink-200 rounded-2xl border bg-white p-4 sm:p-5">
          {lastResult ? (
            <FeedbackPanel
              correct={lastResult.correct}
              feedback={lastResult.feedback}
              scoreDelta={lastResult.score_delta}
              hasSubnode={lastResult.subnode_to_play !== null}
              onNext={player.next}
              onRetry={player.retry}
            />
          ) : currentNode.is_sub && subInfo ? (
            <SubNoeudView
              node={currentNode}
              choix={currentChoix}
              step={subInfo.step}
              total={subInfo.total}
              submitting={player.submitting}
              onSubmit={player.submit}
              paper={paper}
            />
          ) : (
            <NoeudView
              node={currentNode}
              choix={currentChoix}
              indices={dag?.indices ?? []}
              allowHelp={allowHelp}
              submitting={player.submitting}
              onSubmit={player.submit}
              paper={paper}
            />
          )}
        </section>
      )}
    </div>
  );
}

function FeedbackPanel({
  correct,
  feedback,
  scoreDelta,
  hasSubnode,
  onNext,
  onRetry,
}: {
  correct: boolean;
  feedback: string;
  scoreDelta: number;
  hasSubnode: boolean;
  onNext: () => void;
  onRetry: () => void;
}) {
  // Faux SANS chemin développé → on réessaie ; sinon on avance (continuer / décomposer).
  const advance = correct || hasSubnode;
  return (
    <div className="space-y-4">
      <div
        className={cn(
          'flex items-start gap-2 rounded-xl border px-3 py-3 text-sm',
          correct
            ? 'border-xp-200 bg-xp-50 text-xp-800'
            : 'border-accent-200 bg-accent-50 text-accent-800',
        )}
      >
        {correct ? (
          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" />
        ) : (
          <XCircle className="mt-0.5 h-5 w-5 shrink-0" />
        )}
        <div className="flex-1">
          <p className="font-medium">{feedback}</p>
          {scoreDelta > 0 && (
            <p className="text-xp-600 mt-0.5 text-xs font-semibold">+{scoreDelta} pts</p>
          )}
        </div>
      </div>
      {advance ? (
        <Button fullWidth onClick={onNext}>
          {correct ? 'Continuer' : 'Voir le chemin développé'}
        </Button>
      ) : (
        <Button fullWidth variant="secondary" onClick={onRetry}>
          Réessayer
        </Button>
      )}
    </div>
  );
}
